import { SEARCH_DATA_KEY } from 'api/search'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import type { NewDeal, OldTypes, PatchHistoryEntry, Product, User } from 'types'
import { KEY as BOOTSTRAP_KEY, resetBootstrapETag } from '../organizations_table/useAllItemsBootstrap'
import type { AllItems } from '../organizations_table/useAllItemsBootstrap'

interface ApplyPatchPayload {
  PK: string
  SK: string
  deal_uuid: string
  newSemanticVersion: string
  products: Product[]
  currentUser: User
  changeType?: 'patch' | 'minor'
  dealDates?: { startDate: string; endDate: string }
}

interface ApplyPatchResponse {
  deal: NewDeal
  products: Product[]
  patchEntry: PatchHistoryEntry
  message: string
}

export const useApplyPatchVersion = () => {
  const { showSnackbar } = useSnackbarContext()
  const qc = useQueryClient()

  return useMutation<ApplyPatchResponse, Error, ApplyPatchPayload>({
    mutationFn: async (payload) => {
      const response = await axios.post(
        `${baseUrl}/aws/organizations_table/apply-patch-version`,
        payload
      )
      return response.data
    },
    onSuccess: ({ deal, products }, variables) => {
      // 1. Update item cache for this deal
      qc.setQueryData(['item', deal.PK, deal.SK], (prev: any) =>
        prev ? { ...prev, ...deal } : deal
      )

      // 2. Update SEARCH_DATA_KEY — replace deal and products
      qc.setQueryData(SEARCH_DATA_KEY, (prev: OldTypes) => {
        if (!prev) return prev
        const deals = prev.deals.map((d: any) =>
          d.PK === deal.PK && d.SK === deal.SK ? { ...d, ...deal } : d
        )
        const productSKs = new Set(products.map((p: any) => p.SK))
        const otherProducts = (prev.products || []).filter((p: any) => !productSKs.has(p.SK))
        return { ...prev, deals, products: [...otherProducts, ...products] }
      })

      // 3. Update bootstrap cache
      qc.setQueryData(BOOTSTRAP_KEY, (prev: AllItems | undefined) => {
        if (!prev) return prev
        const deals = prev.deals.map((d: any) =>
          d.PK === deal.PK && d.SK === deal.SK ? { ...d, ...deal } : d
        )
        const productSKs = new Set(products.map((p: any) => p.SK))
        const otherProducts = prev.products.filter((p: any) => !productSKs.has(p.SK))
        return { ...prev, deals, products: [...otherProducts, ...products] }
      })

      // 4. Reset bootstrap ETag so next poll fetches fresh data
      resetBootstrapETag(qc)

      // 5. Invalidate product queries for this org
      qc.invalidateQueries({ queryKey: ['product', deal.PK] })

      const label = variables.changeType === 'minor' ? 'Renewal' : 'Patch'
      showSnackbar(`${label} v${deal.semanticVersion} applied successfully`, 'success')
    },
    onError: (error) => {
      showSnackbar(error?.message || 'Failed to apply patch version', 'error')
    },
  })
}
